import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar, Footer } from "../components/Layout";
import { Check, ArrowRight, Sparkles, Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSEO } from "../hooks/useSEO";

const FEATURES = {
  free: [
    "Hiragana + Katakana complets",
    "Test de niveau JLPT",
    "2 leçons N5 offertes",
    "5 messages Sensei IA / jour",
    "Newsletter hebdomadaire",
  ],
  premium: [
    "Toutes les leçons (N5 → N3)",
    "Sensei IA illimité",
    "Exercices générés à l'infini",
    "Suivi de progression complet",
    "Mode hors-ligne (PWA)",
    "Support prioritaire",
  ],
};

const FounderBanner = ({ founderStatus, user, onGoFounder }) => {
  if (!founderStatus?.available) return null;
  if (user?.is_founder || user?.is_lifetime) return null;
  return (
    <section className="max-w-5xl mx-auto px-6 sm:px-12 pt-16" data-testid="founder-banner-pricing">
      <div className="relative rounded-3xl overflow-hidden bg-[#1A2A40] text-white p-8 sm:p-10 border-2 border-[#1A2A40] shadow-brut-lg">
        <div className="absolute -right-6 -top-6 font-jp font-black text-[9rem] text-[#FFD166]/10 leading-none select-none pointer-events-none">創</div>
        <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center relative">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#FFD166] text-[#1A2A40] font-bold text-[11px] uppercase tracking-widest px-3 py-1 rounded-full mb-4">
              <Crown className="w-3.5 h-3.5" /> Offre Fondateur : 100 premiers
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl leading-tight tracking-tight mb-2">
              49 € à vie. <span className="italic font-light text-white/70">Puis 199 € pour tout le monde.</span>
            </h2>
            <p className="text-white/80 max-w-lg leading-relaxed">
              Un paiement unique de 49 € réservé aux 100 premiers étudiants. Ensuite, l'accès à vie repasse au prix
              standard de 199 €. Toutes les leçons, tuteur IA, examens blancs et futures mises à jour inclus, sans abonnement.
            </p>
          </div>
          <button
            onClick={onGoFounder}
            data-testid="founder-cta-pricing"
            className="bg-[#FFD166] text-[#1A2A40] font-bold py-4 px-6 rounded-xl shadow-[6px_6px_0_0_#E94A3F] hover-lift inline-flex items-center justify-center gap-2 whitespace-nowrap"
          >
            Voir l'offre fondateur <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};

const PricingPage = () => {
  const [plans, setPlans] = useState([]);
  const [loadingPlan, setLoadingPlan] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [founderStatus, setFounderStatus] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useSEO({
    title: "Tarifs : Japonais Pas à Pas | 49 € à vie (100 premiers) ou 12,90 €/mois",
    description: "Offre Fondateur : 49 € à vie limitée aux 100 premiers étudiants, puis 199 €. Aussi disponible : 12,90 €/mois ou 89 €/an. 3 jours d'essai sans carte bancaire.",
    canonical: "https://japonais-pas-a-pas.com/pricing",
  });

  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.data)).catch(() => {});
    api.get("/fondateur/status").then((r) => setFounderStatus(r.data)).catch(() => {});
  }, []);

  const start = async (planId, promoOverride) => {
    if (!user) {
      toast.info("Créez votre compte (essai 3j gratuit) puis revenez ici.");
      nav("/register");
      return;
    }
    setLoadingPlan(planId);
    try {
      const { data } = await api.post("/checkout/session", {
        plan_id: planId,
        origin_url: window.location.origin,
        promo_code: promoOverride || (promoCode.trim() || undefined),
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur de paiement");
      setLoadingPlan(null);
    }
  };

  return (
    <div className="washi-bg min-h-screen" data-testid="pricing-page">
      <Navbar />
      <FounderBanner founderStatus={founderStatus} user={user} onGoFounder={() => nav("/fondateur")} />
      <PricingSection plans={plans} onStart={start} loadingPlan={loadingPlan} user={user} hero
                      promoCode={promoCode} setPromoCode={setPromoCode} />
      <Footer />
    </div>
  );
};

export const PricingSection = ({ plans, onStart, loadingPlan, user, hero = false, promoCode, setPromoCode }) => {
  const [founderAvailable, setFounderAvailable] = useState(false);
  useEffect(() => {
    api.get("/fondateur/status")
      .then((r) => setFounderAvailable(!!r.data?.available))
      .catch(() => setFounderAvailable(false));
  }, []);
  const isUserFounder = !!(user?.is_founder || user?.is_lifetime);
  const founderActive = founderAvailable && !isUserFounder;

  return (
  <section className="max-w-7xl mx-auto px-6 sm:px-12 py-20" data-testid="pricing-section">
    {hero && (
      <div className="text-center max-w-2xl mx-auto mb-14">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-3">Tarifs</div>
        <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-[1] text-[#1A2A40] mb-5">
          Un seul abonnement.<br /><span className="italic font-light">Tout le japonais.</span>
        </h1>
        <p className="text-lg text-[#4A5568]">
          3 jours d'essai offert dès l'inscription, sans engagement. Annulation par email.
        </p>
      </div>
    )}
    {!hero && (
      <div className="text-center max-w-2xl mx-auto mb-14">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-3">Tarifs</div>
        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-[1] text-[#1A2A40] mb-4">
          Un seul abonnement.<br /><span className="italic font-light">Tout le japonais.</span>
        </h2>
        <p className="text-lg text-[#4A5568]">3 jours d'essai à l'inscription. Annulation par email.</p>
      </div>
    )}

    <div className="grid md:grid-cols-3 gap-6">
      {plans.map((p) => {
        const popular = p.popular;
        // Founder deal: lifetime tier shows 49 € (with 199 € struck through)
        // while founder offer is still open. Checkout uses FONDATEUR50 promo
        // so the actual charge matches the displayed price.
        const showFounderPrice = founderActive && p.is_lifetime;
        return (
          <div
            key={p.id}
            data-testid={`plan-card-${p.id}`}
            className={`relative rounded-3xl p-8 border-2 ${popular ? "bg-[#1A2A40] text-white border-[#1A2A40] shadow-brut-lg scale-[1.02] md:scale-105" : "bg-[#FDFBF7] text-[#1A2A40] border-[#1A2A40]/15"}`}
          >
            {popular && (
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#FFD166] text-[#1A2A40] font-bold text-[11px] uppercase tracking-widest px-4 py-1.5 rounded-full border-2 border-[#1A2A40]">
                ⭐ Populaire · −43%
              </div>
            )}
            {showFounderPrice && (
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-[#FFD166] text-[#1A2A40] font-bold text-[11px] uppercase tracking-widest px-4 py-1.5 rounded-full border-2 border-[#1A2A40]" data-testid="founder-ribbon">
                👑 Offre Fondateur
              </div>
            )}
            <div className={`text-xs font-bold uppercase tracking-widest mb-3 ${popular ? "text-[#FFD166]" : "text-[#E94A3F]"}`}>
              {p.label}
            </div>
            {showFounderPrice ? (
              <>
                <div className="flex items-baseline gap-2 mb-1" data-testid="lifetime-founder-price">
                  <span className="font-display font-black text-5xl tracking-tighter">49 €</span>
                  <span className="text-sm text-[#4A5568]">à vie</span>
                </div>
                <div className="text-sm text-[#4A5568] line-through mb-2" data-testid="lifetime-normal-price">
                  199 € à vie
                </div>
                <p className="text-sm text-[#1A2A40] mb-6 font-medium">
                  Offre fondateur, réservée aux 100 premiers. Ensuite 199 €.
                </p>
              </>
            ) : (
              <>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="font-display font-black text-5xl tracking-tighter">
                    {p.amount.toFixed(2).replace(".", ",")} €
                  </span>
                  {!p.is_lifetime && (
                    <span className={`text-sm ${popular ? "text-white/70" : "text-[#4A5568]"}`}>
                      / {p.id === "annual" ? "an" : "mois"}
                    </span>
                  )}
                </div>
                <p className={`text-sm mb-6 ${popular ? "text-white/80" : "text-[#4A5568]"}`}>{p.tag}</p>
              </>
            )}

            <ul className="space-y-2.5 mb-8">
              {FEATURES.premium.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <Check className={`w-4 h-4 mt-0.5 shrink-0 ${popular ? "text-[#FFD166]" : "text-[#8CA07A]"}`} />
                  <span className={popular ? "text-white/90" : "text-[#1A2A40]"}>{f}</span>
                </li>
              ))}
            </ul>

            <button
              onClick={() => onStart(p.id, showFounderPrice ? "FONDATEUR50" : undefined)}
              disabled={loadingPlan === p.id}
              data-testid={`plan-cta-${p.id}`}
              className={`w-full font-bold py-3.5 rounded-xl inline-flex items-center justify-center gap-2 transition-all disabled:opacity-60 ${
                popular
                  ? "bg-[#E94A3F] text-white shadow-brut-sm hover-lift"
                  : "bg-[#1A2A40] text-white hover:bg-black"
              }`}
            >
              {loadingPlan === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <>
                  {p.id === "monthly" ? "Démarrer (essai 3j)" : p.id === "lifetime" ? "Acheter à vie" : "S'abonner"}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        );
      })}
    </div>

    <p className="text-center mt-10 text-sm text-[#4A5568] max-w-xl mx-auto">
      Paiement sécurisé par Stripe · Carte bancaire, Apple Pay, Google Pay · Aucune publicité.
      {user && user.is_premium && user.premium_status === "trial" && (
        <span className="block mt-2 text-[#E94A3F] font-bold">
          🎁 Vous êtes actuellement en essai gratuit jusqu'au {new Date(user.trial_ends_at).toLocaleDateString("fr-FR")}.
        </span>
      )}
    </p>

    {setPromoCode && (
      <div className="max-w-md mx-auto mt-8" data-testid="promo-section">
        <label className="block text-xs font-bold uppercase tracking-widest text-[#4A5568] mb-2 text-center">
          Code promo (optionnel)
        </label>
        <input
          type="text"
          value={promoCode || ""}
          onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
          placeholder="NIHONGO20"
          data-testid="promo-input"
          className="w-full bg-[#FDFBF7] border-2 border-[#1A2A40]/20 rounded-xl px-4 py-3 text-center font-mono uppercase tracking-widest focus:outline-none focus:border-[#E94A3F]"
        />
        <p className="text-center text-xs text-[#A0AEC0] mt-2">−20% sur l'annuel</p>
      </div>
    )}

    <div className="mt-14 grid md:grid-cols-3 gap-4 max-w-3xl mx-auto text-center text-sm text-[#4A5568]">
      <div>🔒 <strong>Sans engagement</strong>, annulation par email</div>
      <div>💸 <strong>Remboursé 14 jours</strong>, sans question</div>
      <div>🇫🇷 <strong>Support en français</strong> : &lt;24h</div>
    </div>
  </section>
  );
};

export default PricingPage;
